import { createAttractionThemeTranslationsConfig, type AttractionThemeTranslation } from '@/config/objects/attraction-theme-translations';
import { CrudFormView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Attraction theme translation form page (edit only)
 */
export function AttractionThemeTranslationFormPage() {
  const { attractionId, themeId, translationId } = useParams<{ attractionId: string; themeId: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const translationsConfig = useMemo(
    () => (attractionId && themeId ? createAttractionThemeTranslationsConfig(attractionId, themeId) : null),
    [attractionId, themeId],
  );

  // Fetch translation data for breadcrumbs (only in edit mode)
  const { data: translationData } = useQuery({
    queryKey: ['ThemeTranslation', attractionId, themeId, translationId],
    queryFn: async () => {
      if (!translationId) return null;
      const response = await apiClient.get<{ data: AttractionThemeTranslation }>(
        `/attractions/${attractionId}/themes/${themeId}/translations/${translationId}`
      );
      return response.data;
    },
    enabled: !!translationId && !!attractionId && !!themeId,
  });

  const breadcrumbs = useMemo(() => [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: translationData?.theme?.attraction?.name || 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Themes', href: `/attractions/${attractionId}` },
    { label: translationData?.theme?.name || translationData?.theme?.themeType?.name || 'Theme', href: `/attractions/${attractionId}/themes/${themeId}` },
    { label: 'Translations', href: `/attractions/${attractionId}/themes/${themeId}` },
    { label: translationId ? 'Edit Translation' : 'New Translation', current: true },
  ], [translationData, attractionId, themeId, translationId]);

  if (!attractionId || !themeId || !translationsConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const handleSuccess = () => {
    navigate(`/attractions/${attractionId}/themes/${themeId}/translations/${translationId}`);
  };

  return (
    <PageContainer>
      <CrudFormView
        config={translationsConfig}
        itemId={translationId}
        breadcrumbs={breadcrumbs}
        onSuccess={handleSuccess}
      />
    </PageContainer>
  );
}
